import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Route {
  id: string
  path: string
  service_name: string
  target_url: string
  methods: string[]
  auth_required: boolean
  status: string
  requests_today: number
  avg_latency_ms: number
  error_rate: number
}

interface APIKey {
  id: string
  name: string
  key_prefix: string
  tier: string
  scopes: string[]
  status: string
  requests_today: number
  last_used_at: string
}

export default function APIGatewayPage() {
  const [routes, setRoutes] = useState<Route[]>([])
  const [apiKeys, setApiKeys] = useState<APIKey[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [metrics, setMetrics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [routesRes, keysRes, statsRes, metricsRes] = await Promise.all([
      fetch(API_BASE + '/api-gateway/routes'),
      fetch(API_BASE + '/api-gateway/api-keys'),
      fetch(API_BASE + '/api-gateway/statistics'),
      fetch(API_BASE + '/api-gateway/metrics')
    ])
    if (routesRes.ok) setRoutes((await routesRes.json()).routes || [])
    if (keysRes.ok) setApiKeys((await keysRes.json()).api_keys || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (metricsRes.ok) setMetrics(await metricsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'maintenance': return 'bg-yellow-100 text-yellow-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'enterprise': return 'bg-purple-100 text-purple-800'
      case 'professional': return 'bg-blue-100 text-blue-800'
      case 'basic': return 'bg-green-100 text-green-800'
      case 'free': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">API Gateway Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Route</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Routes</div>
              <div className="text-2xl font-bold">{statistics.total_routes}</div>
              <div className="text-xs text-green-600">{statistics.active_routes} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">API Keys</div>
              <div className="text-2xl font-bold">{statistics.total_api_keys}</div>
              <div className="text-xs text-green-600">{statistics.active_keys} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Requests Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_requests_today?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Latency</div>
              <div className="text-2xl font-bold">{statistics.average_latency_ms}ms</div>
              <div className="text-xs text-gray-500">{statistics.error_rate}% errors</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">API Routes</h2></div>
            <div className="divide-y">
              {routes.slice(0, 6).map(route => (
                <div key={route.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold font-mono text-sm">{route.path}</div>
                      <div className="text-sm text-gray-500">{route.service_name}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(route.status)}`}>{route.status}</span>
                  </div>
                  <div className="flex gap-1 mb-2">
                    {route.methods.map(method => (
                      <span key={method} className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{method}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
                    <div>{route.requests_today.toLocaleString()} requests</div>
                    <div>{route.avg_latency_ms}ms latency</div>
                    <div>{route.error_rate}% errors</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">API Keys</h3>
              <div className="space-y-3">
                {apiKeys.slice(0, 5).map(key => (
                  <div key={key.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">{key.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getTierColor(key.tier)}`}>{key.tier}</span>
                    </div>
                    <div className="text-xs text-gray-500 font-mono mb-1">{key.key_prefix}...</div>
                    <div className="text-xs text-gray-500">{key.requests_today.toLocaleString()} requests today</div>
                  </div>
                ))}
              </div>
            </div>

            {metrics && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Traffic by Status</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-green-600">2xx Success</span>
                    <span className="font-medium">{metrics.requests_by_status?.['2xx']?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-600">4xx Client Errors</span>
                    <span className="font-medium">{metrics.requests_by_status?.['4xx']?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-600">5xx Server Errors</span>
                    <span className="font-medium">{metrics.requests_by_status?.['5xx']?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
